/**
 * Makros (Spec 3.2 / 8.2): eine **geordnete** Aktionsliste, die ein Agent per
 * Klick auf ein Ticket anwendet.
 *
 * Die Reihenfolge ist fachlich relevant und deshalb Teil des Schemas: „Tag
 * setzen, dann zuweisen, dann auf `pending`" ist etwas anderes als dieselben
 * Aktionen rueckwaerts. `actions` ist ein Array, kein Objekt.
 *
 * Zwei Aktionen fallen aus dem Rahmen und sind deshalb hier dokumentiert:
 *
 * | Aktion | Warum sie anders ist |
 * |---|---|
 * | `insert_snippet` | aendert nichts am Ticket, sondern **fuellt den Editor** — das passiert im Browser, der Server gibt nur die Snippet-ID zurueck |
 * | `create_skilllock_task` | ruft ein fremdes System (Spec 10) — laeuft als Job **nach** dem Commit, nie in der Transaktion |
 *
 * Alle uebrigen Aktionen laufen ueber die bestehenden Services
 * (`applyStatus`, `updateTicket`, `setTicketTags`) und damit in **einer**
 * Transaktion: entweder das Makro geht ganz durch oder gar nicht.
 *
 * Doku: docs/services/macros.md
 */
import { z } from 'zod';
import { uuidSchema } from './common.js';
import { ticketPrioritySchema, ticketStatusSchema } from './ticket.js';
export const MACRO_ACTION_TYPES = [
    'set_status',
    'set_priority',
    'assign',
    'add_tag',
    'insert_snippet',
    'create_skilllock_task',
];
export const macroActionTypeSchema = z.enum(MACRO_ACTION_TYPES);
/**
 * Zuweisung: Mitglied **oder** Team, oder beides `null` („Zuweisung
 * aufheben"). Beides gleichzeitig ist erlaubt — Spec 8.1 kennt die
 * Team-Zuweisung mit spaeterer Uebernahme durch ein Mitglied.
 */
export const macroAssignActionSchema = z.object({
    type: z.literal('assign'),
    member_id: uuidSchema.nullable().default(null),
    team_id: uuidSchema.nullable().default(null),
});
export const macroActionSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('set_status'), status: ticketStatusSchema }),
    z.object({ type: z.literal('set_priority'), priority: ticketPrioritySchema }),
    macroAssignActionSchema,
    z.object({ type: z.literal('add_tag'), tag_id: uuidSchema }),
    z.object({ type: z.literal('insert_snippet'), snippet_id: uuidSchema }),
    /**
     * `title` leer = Betreff des Tickets. `board_id` leer = das in den
     * SkillLock-Einstellungen des Mandanten hinterlegte Standard-Board.
     */
    z.object({
        type: z.literal('create_skilllock_task'),
        title: z.string().max(300).nullable().default(null),
        board_id: z.string().max(200).nullable().default(null),
    }),
]);
export const macroSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    title: z.string().min(1).max(200),
    description: z.string().max(500),
    actions: z.array(macroActionSchema),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
export const createMacroSchema = z.object({
    title: z.string().min(1).max(200),
    description: z.string().max(500).default(''),
    // Mindestens eine Aktion — ein Makro ohne Wirkung ist ein Bedienfehler.
    actions: z.array(macroActionSchema).min(1).max(20),
});
export const updateMacroSchema = createMacroSchema.partial();
/**
 * Was nach einem Makro-Lauf zurueckkommt. `insert_snippet_ids` sind die
 * Bausteine, die der Browser in den Antwort-Editor legen soll — der Server
 * schreibt keine Nachricht (Invariante 3 sinngemaess: Text an den Kunden
 * entsteht nur durch einen Agent-Klick auf „Senden").
 */
export const macroRunResultSchema = z.object({
    macro_id: uuidSchema,
    applied: z.array(macroActionTypeSchema),
    insert_snippet_ids: z.array(uuidSchema),
    /** Wie viele SkillLock-Tasks als Job eingereiht wurden (Spec 10). */
    skilllock_tasks_queued: z.number().int().min(0),
});
//# sourceMappingURL=macro.js.map